/* eslint-disable react/no-multi-comp */
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { TranslationService } from '@/utils/services/TranslationService';

import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import useToggle from '@/utils/hooks/utils/useToggle';

import {
    getTabsList,
    TAB_PINS_PINTEREST,
    TAB_POST_FACEBOOK,
    TAB_POST_INSTAGRAM,
    TAB_POST_LINKEDIN,
    TAB_POST_TIKTOK,
    TAB_POST_YOUTUBE,
    TAB_REPLIES_TWITTER,
    TAB_STORIES_INSTAGRAM,
    TAB_TWEETS_TWITTER
} from '@/lib/myprofile/tabs';
import { CONTENT_POSTS } from '@/lib/routes/privateRoutes';
import { INSTAGRAM } from '@/lib/socialProfile';

import NavTabsButtons from '@/components/composite/NavTabs/NavTabButtons';
import { WrapperSwitchLayout } from '@/components/layouts/wrappers/WrapperSwitchLayout';
import SelectSortBy from '@/components/pages/myprofile/filters/SelectSortBy';
import SelectStoriesType from '@/components/pages/myprofile/filters/SelectStoriesType';
import PinsPinterestTab from '@/components/pages/myprofile/tabs/PinsPinterestTab';
import PostsFacebookTab from '@/components/pages/myprofile/tabs/PostsFacebookTab';
import PostsInstagramTab from '@/components/pages/myprofile/tabs/PostsInstagramTab';
import PostsLinkedinTab from '@/components/pages/myprofile/tabs/PostsLinkedinTab';
import PostsTiktokTab from '@/components/pages/myprofile/tabs/PostsTiktokTab';
import PostsYoutubeTab from '@/components/pages/myprofile/tabs/PostsYoutubeTab';
import RepliesTwitterTab from '@/components/pages/myprofile/tabs/RepliesTwitterTab';
import StoriesInstagramTab from '@/components/pages/myprofile/tabs/StoriesInstagramTab';
import TweetsTwitterTab from '@/components/pages/myprofile/tabs/TweetsTwitterTab';
import { PageContentWrapper } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper';
import { PageHeaderRecap } from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap';
import ButtonIcons from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap/ButtonIcons';

const ContentPage = ({
    selectedTab,
    isFiltersOpen,
    toggleFilters,
    isExportModalOpen,
    toggleExportModal,
    renderHeader
}) => {
    const tabProps = {
        isFiltersOpen,
        toggleFilters,
        isExportModalOpen,
        toggleExportModal,
        header: renderHeader
    };

    if (selectedTab === TAB_POST_INSTAGRAM) {
        return <PostsInstagramTab {...tabProps} />;
    }

    if (selectedTab === TAB_STORIES_INSTAGRAM) {
        return <StoriesInstagramTab {...tabProps} />;
    }

    if (selectedTab === TAB_POST_FACEBOOK) {
        return <PostsFacebookTab {...tabProps} />;
    }

    if (selectedTab === TAB_POST_LINKEDIN) {
        return <PostsLinkedinTab {...tabProps} />;
    }

    if (selectedTab === TAB_PINS_PINTEREST) {
        return <PinsPinterestTab {...tabProps} />;
    }

    if (selectedTab === TAB_TWEETS_TWITTER) {
        return <TweetsTwitterTab {...tabProps} />;
    }

    if (selectedTab === TAB_REPLIES_TWITTER) {
        return <RepliesTwitterTab {...tabProps} />;
    }

    if (selectedTab === TAB_POST_TIKTOK) {
        return <PostsTiktokTab {...tabProps} />;
    }

    if (selectedTab === TAB_POST_YOUTUBE) {
        return <PostsYoutubeTab {...tabProps} />;
    }

    return null;
};

const Page = () => {
    const { t: tCommons } = useTranslation('commons');
    const { t: tMyprofile } = useTranslation('myprofile');

    const { isOpen: isFiltersOpen, toggle: toggleFilters } = useToggle({ initial: true });
    const { isOpen: isExportModalOpen, toggle: toggleExportModal } = useToggle();

    const { activeSocialProfile, setActiveSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles();
    const tabsList = getTabsList(tCommons);
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const { tab, socialProfileId } = router.query;
    const [selectedTab, setSelectedTab] = useState(
        tab
            ? tabsList[activeSocialProfile?.social_network_type].filter(
                  (tabElement) => tabElement.id === tab
              )[0]?.id
            : tabsList[activeSocialProfile?.social_network_type ?? INSTAGRAM][0].id
    );

    useEffect(() => {
        if (
            !tabsList[activeSocialProfile?.social_network_type ?? INSTAGRAM].filter(
                (item) => item.id === selectedTab
            ).length
        ) {
            setSelectedTab(tabsList[activeSocialProfile?.social_network_type ?? INSTAGRAM][0].id);
        }
        if (tab) {
            const newUrl = deleteQuery(['tab', 'socialProfileId']);
            router.replace(newUrl);
        }
    }, [activeSocialProfile?.social_network_type, selectedTab, setSelectedTab, tabsList]);

    // Switch to the correct social profile id if we come from a dashboard widget
    useEffect(() => {
        if (socialProfileId) {
            let socialProfile = activatedSocialProfiles.find(
                (sp) => sp.id === Number(socialProfileId)
            );
            setActiveSocialProfile(socialProfile);
        }
    }, []);

    return (
        <>
            <PageHeaderRecap title={tCommons('menu.all_posts')}>
                {({ isSticky }) => (
                    <ButtonIcons
                        actions={[
                            {
                                id: 'export',
                                icon: 'export',
                                isActive: isExportModalOpen,
                                ['data-testid']: 'export-button',
                                tooltip: tMyprofile('export_xls.XLS_export'),
                                onClick: toggleExportModal
                            },
                            {
                                id: 'filter',
                                icon: 'filter',
                                isActive: isFiltersOpen,
                                tooltip: tMyprofile('filters'),
                                onClick: toggleFilters,
                                available:
                                    selectedTab !== TAB_REPLIES_TWITTER &&
                                    selectedTab !== TAB_STORIES_INSTAGRAM
                            }
                        ]}
                        smaller={isSticky}
                    />
                )}
            </PageHeaderRecap>
            <PageContentWrapper>
                <ContentPage
                    selectedTab={selectedTab}
                    isFiltersOpen={isFiltersOpen}
                    toggleFilters={toggleFilters}
                    isExportModalOpen={isExportModalOpen}
                    toggleExportModal={toggleExportModal}
                    renderHeader={(setRequestParams, postType) => (
                        <div className="mb-5 flex flex-col justify-between space-y-5 lg:flex-row lg:space-y-0">
                            <NavTabsButtons
                                tabs={
                                    tabsList[activeSocialProfile?.social_network_type ?? INSTAGRAM]
                                }
                                activeTabId={selectedTab}
                                setActiveTabId={setSelectedTab}
                            />
                            <div className="flex flex-col space-y-3 lg:flex-row lg:space-x-3 lg:space-y-0">
                                {selectedTab === TAB_STORIES_INSTAGRAM ? (
                                    <div className="w-full lg:w-40">
                                        <SelectStoriesType setRequestParams={setRequestParams} />
                                    </div>
                                ) : null}
                                <div className="w-full lg:w-48">
                                    <SelectSortBy
                                        setRequestParams={setRequestParams}
                                        postType={postType}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                />
            </PageContentWrapper>
        </>
    );
};

const Myprofile = () => {
    return (
        <WrapperSwitchLayout>
            <Page />
        </WrapperSwitchLayout>
    );
};

export const getStaticProps = TranslationService.getTranslationMessagesFromRoute(CONTENT_POSTS);

export default Myprofile;
